import { Box } from "@dropbox/dig-foundations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Continue_DashSelfServeOnboarding } from "@mirage/analytics/events/types/continue_dash_self_serve_onboarding";
import { PAP_View_DashSelfServeOnboarding } from "@mirage/analytics/events/types/view_dash_self_serve_onboarding";
import type { ProfilingQuestionKindOfWorkAnswer } from "@mirage/service-onboarding-surveys/service/onboarding-surveys/self-serve-profiling";
import { connectionsByTypeAtom } from "@mirage/shared/atoms/connections";
import {
  getConnectionMetadata,
  isUIConnection,
} from "@mirage/shared/connectors/util";
import { useDoOnceOnMount } from "@mirage/shared/hooks/useDoOnceOnMount";
import { showSnackbar } from "@mirage/shared/snackbar";
import type { UIConnection, UIConnector } from "@mirage/shared/types";
import i18n from "@mirage/translations";
import { useAtomValue } from "jotai";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { OnboardingConnectorsSkeleton } from "../flows/OnboardingConnectorsSkeleton";
import { OnboardingConnectorsV2 } from "../flows/OnboardingConnectorsV2";
import {
  isDropboxConnector,
  useDropboxAutoConnect,
} from "../hooks/useDropboxAutoConnect";
import { useOnboardingSurveys } from "../hooks/useOnboardingSurveys";
import { useSelfServeOnboardingState } from "../hooks/useSelfServeOnboardingState";
import { useSuggestedConnectors } from "../hooks/useSuggestedConnectors";
import { AddConnectionsModal } from "./AddConnectionsModal";
import { ConnectorsModal } from "./ConnectorsModal";

import { useSelfServeProfilingSurveyName } from "../hooks/useSelfServeProfilingSurveyName";
import styles from "./OnboardingConnectorsStep.module.css";
import { SelfServeOnboardingStep } from "./types";

type Props = {
  isAdmin: boolean;
  canProvisionDashFreemiumTeam: boolean;
  onContinue: () => void;
  onValidationChange: (valid: boolean) => void;
  nextStep?: SelfServeOnboardingStep;
};

const MINIMUM_CONNECTIONS = 1;

export const OnboardingConnectorsStep = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (
    {
      isAdmin,
      canProvisionDashFreemiumTeam,
      onContinue,
      onValidationChange,
      nextStep,
    }: Props,
    ref,
  ) => {
    const { reportPapEvent } = useMirageAnalyticsContext();
    const [displayAppsModal, setDisplayAppsModal] = useState(false);
    const [displayAddAppsModal, setDisplayAddAppsModal] = useState(false);
    const connectionsByType = useAtomValue(connectionsByTypeAtom);

    const { surveyName } = useSelfServeProfilingSurveyName(
      isAdmin,
      canProvisionDashFreemiumTeam,
    );

    useDoOnceOnMount(() => {
      reportPapEvent(
        PAP_View_DashSelfServeOnboarding({
          selfServeIsTeamAdmin: isAdmin,
          onboardingStep: "create_connectors",
          featureLine: "onboarding",
        }),
      );
    });

    const { surveyResponses } = useOnboardingSurveys(surveyName);
    const { surveyResponses: surveyResponsesState } =
      useSelfServeOnboardingState();

    const surveyAnswersForSurveyNameState =
      surveyResponsesState.get(surveyName) || [];
    const answerForKindOfWorkState = surveyAnswersForSurveyNameState.find(
      (answer) => answer.surveyQuestionId === "KIND_OF_WORK_V1",
    );

    const kindOfWorkAnswerFromState = answerForKindOfWorkState
      ? answerForKindOfWorkState.surveyAnswer
      : null;

    // If no answer is found in surveyResponsesState, fall back to surveyResponses.
    const surveyAnswersForSurveyName = surveyResponses || [];
    const answerForKindOfWork = surveyAnswersForSurveyName.find(
      (answer) => answer.surveyQuestionId === "KIND_OF_WORK_V1",
    );

    const kindOfWorkAnswer =
      kindOfWorkAnswerFromState || answerForKindOfWork?.surveyAnswer;

    const { suggestedConnectors, connectors, allConnectors, loading } =
      useSuggestedConnectors(
        kindOfWorkAnswer as ProfilingQuestionKindOfWorkAnswer,
      );

    useEffect(() => {
      onValidationChange(true);
    }, [isAdmin, reportPapEvent, onValidationChange]);

    const handleShowAllApps = useCallback(() => {
      setDisplayAppsModal(true);
    }, []);

    const handleAppsModalClose = useCallback(
      (skip: boolean) => {
        setDisplayAddAppsModal(false);
        if (skip) {
          reportPapEvent(
            PAP_Continue_DashSelfServeOnboarding({
              selfServeIsTeamAdmin: isAdmin,
              onboardingStep: "add_connectors",
              featureLine: "onboarding",
            }),
          );
          onContinue();
        }
      },
      [onContinue, reportPapEvent, isAdmin],
    );
    const {
      dropboxAutoConnectState,
      setDropboxAutoConnectState,
      handleDropboxAutoConnect,
    } = useDropboxAutoConnect(allConnectors);

    const handleSubmitSurvey = useCallback(async () => {
      const connections = connectors.filter((connectorOrConnection) => {
        const isConnection = isUIConnection(connectorOrConnection);
        if (!isConnection) return false;
        const { type: connectionType } = getConnectionMetadata(
          connectorOrConnection,
        );
        if (!connectionType) return false;
        const connected = !!connectionsByType[connectionType];
        return connected;
      });

      if (dropboxAutoConnectState === "connected") {
        await handleDropboxAutoConnect(
          nextStep || SelfServeOnboardingStep.TEAM_INVITE,
        );
        return true;
      }

      if (connections.length >= MINIMUM_CONNECTIONS) {
        return true;
      }

      setDisplayAddAppsModal(true);
      return false;
    }, [
      connectors,
      dropboxAutoConnectState,
      connectionsByType,
      handleDropboxAutoConnect,
      nextStep,
    ]);

    useImperativeHandle(ref, () => ({
      submitSurvey: () => handleSubmitSurvey(),
    }));

    const connectedConnectorsWithAutoConnectedDropbox = useMemo(() => {
      if (dropboxAutoConnectState === "connected") {
        const dropboxConnector = allConnectors?.find((c) => {
          return "id_attrs" in c && c.id_attrs?.type === "dropbox";
        });

        if (!dropboxConnector) return connectors;

        // Force the Dropbox connector to be team-owned if auto-connect is enabled
        return [
          {
            ...dropboxConnector,
            metadata: {
              ...((dropboxConnector as UIConnection).metadata ?? {}),
              owner: {
                owner: { ".tag": "team" },
              },
            },
          },
          ...connectors,
        ];
      }
      return connectors;
    }, [connectors, dropboxAutoConnectState, allConnectors]);

    const filterCompanyConnectorsFromNonAdmins = useCallback(
      (connector: UIConnector | UIConnection) => {
        const isConnector = "details" in connector;
        const isDropbox = isDropboxConnector(connector);

        if (isConnector) {
          const supportsTeamAuth = Boolean(
            connector.details?.supports_team_auth,
          );
          if (supportsTeamAuth) {
            return isAdmin && isDropbox; // Only allow Dropbox connector if admin
          }
          return true;
        }

        // Must be a UIConnection
        if ("metadata" in connector) {
          const ownerTag = connector.metadata?.owner?.owner?.[".tag"];
          return ownerTag !== "team"; // Hide if team-owned
        }

        return true;
      },
      [isAdmin],
    );

    const filterDropboxConnectorIfAutoConnectEnabled = useCallback(
      (connector: UIConnector | UIConnection) => {
        if (dropboxAutoConnectState === "connected") {
          return !isDropboxConnector(connector);
        }
        return true;
      },
      [dropboxAutoConnectState],
    );

    if (loading) {
      return <OnboardingConnectorsSkeleton />;
    }

    return (
      <>
        <Box
          display="flex"
          flexDirection="column"
          className={styles.connectorsContainer}
        >
          {connectedConnectorsWithAutoConnectedDropbox?.filter(
            filterCompanyConnectorsFromNonAdmins,
          ).length > 0 && (
            <OnboardingConnectorsV2
              title={i18n.t("onboarding_connectors_section_added")}
              subheader={i18n.t(
                "onboarding_connectors_section_added_select_to_remove",
              )}
              connectors={connectedConnectorsWithAutoConnectedDropbox.filter(
                filterCompanyConnectorsFromNonAdmins,
              )}
              browserExtensionData={[]}
              showSnackbar={showSnackbar}
              showConnectorDescription
              onDropboxAutoConnectRemoved={
                dropboxAutoConnectState !== "disabled"
                  ? () => {
                      setDropboxAutoConnectState("disconnected");
                    }
                  : undefined
              }
            />
          )}
          <OnboardingConnectorsV2
            title={i18n.t("onboarding_connectors_section_suggested")}
            connectors={suggestedConnectors
              .filter(filterDropboxConnectorIfAutoConnectEnabled)
              .filter(filterCompanyConnectorsFromNonAdmins)}
            browserExtensionData={[]}
            showSnackbar={showSnackbar}
            showShowAllApps
            onShowAllAppsClick={handleShowAllApps}
            onDropboxAutoConnectConnected={
              dropboxAutoConnectState !== "disabled"
                ? () => setDropboxAutoConnectState("connected")
                : undefined
            }
          />
        </Box>
        <ConnectorsModal
          connectors={allConnectors
            ?.filter(filterDropboxConnectorIfAutoConnectEnabled)
            .filter(filterCompanyConnectorsFromNonAdmins)}
          open={displayAppsModal}
          onClose={() => setDisplayAppsModal(false)}
          onDropboxAutoConnectConnected={
            dropboxAutoConnectState !== "disabled"
              ? () => setDropboxAutoConnectState("connected")
              : undefined
          }
        />
        <AddConnectionsModal
          open={displayAddAppsModal}
          onClose={handleAppsModalClose}
        />
      </>
    );
  },
);

OnboardingConnectorsStep.displayName = "OnboardingConnectorsStep";
